<template>
	<div
		class="payment-group-card"
		:class="{
			'-selected': selected,
			'-selectable': selectable,
			'-t-light': lightTheme,
		}"
		@click="$emit('select')"
	>
		<div class="payment-group-card__header">
			<h3 class="payment-group-card__title">
				{{ title }}
			</h3>
			<div class="payment-group-card__price">
				<span class="payment-group-card__price-from">
					{{ $I18n.trans('payments.price_from') }}
				</span>
				<div class="payment-group-card__price-value">
					{{ minPrice }}
				</div>
				<span class="payment-group-card__price-currency">{{ currencyCode }}</span>
			</div>
		</div>
		<div class="payment-group-card__features">
			<div
				v-for="feature in features"
				:key="feature.label"
				class="payment-group-card__feature"
			>
				<Icon
					:icon-name="feature.icon || 'c-info'"
					size="sm"
					class="payment-group-card__feature-icon"
				/>
				<span class="payment-group-card__feature-label">
					{{ feature.label }}
				</span>
			</div>
		</div>
		<div class="payment-group-card__footer">
			<div class="payment-group-card__divider" />
			<div
				v-if="info"
				class="payment-group-card__info"
			>
				{{ info }}
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon.vue';

export default {
	name:       'PaymentGroupCard',
	components: {
		Icon,
	},
	props: {
		title: {
			type:     String,
			required: true,
		},
		info: {
			type:    String,
			default: null,
		},
		minPrice: {
			type:     String,
			required: true,
		},
		currencyCode: {
			type:     String,
			required: true,
		},
		features: {
			type:    Array,
			default: () => [],
		},
		selected: {
			type:    Boolean,
			default: false,
		},
		selectable: {
			type:    Boolean,
			default: true,
		},
		lightTheme: {
			type:    Boolean,
			default: false,
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.payment-group-card {
	--color-payment-card-background: var(--color-grey-900);
	--color-payment-card-text: var(--color-soft-white);
	--color-payment-card-text-price: var(--color-grey-400);
	--color-payment-card-text-info: var(--color-grey-200);
	--color-payment-card-divider: var(--color-grey-800);
	--color-payment-card-border: var(--color-grey-700);

	&.-t-light {
		--color-payment-card-background: var(--color-grey-950);
		--color-payment-card-text: var(--color-black);
		--color-payment-card-border: var(--color-grey-800);
	}

	&.-selected {
		--color-payment-card-background: var(--color-grey-400);
		--color-payment-card-text: var(--color-grey-950);
		--color-payment-card-text-price: var(--color-grey-950);
		--color-payment-card-text-info: var(--color-grey-950);
		--color-payment-card-divider: var(--color-grey-600);

		&.-t-light {
			--color-payment-card-background: var(--color-grey-850);
			--color-payment-card-text: var(--color-black);
			--color-payment-card-text-price: var(--color-grey-400);
			--color-payment-card-text-info: var(--color-grey-400);
		}
	}

	background-color: var(--color-payment-card-background); // stylelint-disable-line
	border-radius: $border-radius--lg;
	min-width: 200px;
	max-width: 250px;
	padding-top: $sp5;
	padding-bottom: $sp5;
	padding-left: $sp4-5;
	padding-right: $sp4-5;
	display: flex;
	flex-direction: column;
	align-items: center;
	flex-grow: 1;
	justify-content: space-between;
	transition: background-color $trans-time-fast, border-color $trans-time-fast;
	border: 2px solid transparent;

	&.-selectable {
		cursor: pointer;
	}

	&:hover {
		&.-selectable:not(.-selected) {
			border-color: var(--color-payment-card-border);
		}
	}
}

.payment-group-card__title {
	@include font(primary, light, normal, fs-150);
	text-align: center;
	margin-bottom: $sp4;
	color: var(--color-payment-card-text);
	transition: color $trans-time-fast;
}

.payment-group-card__price {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: fit-content;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: $sp6;
	color: var(--color-payment-card-text-price);
	transition: color $trans-time-fast;
}

.payment-group-card__price-from {
	@include font-size(fs-80);
	align-self: flex-start;
}

.payment-group-card__price-value {
	@include font(primary, bold, normal, fs-190);
	line-height: 1;
	align-self: flex-end;
	display: flex;
	align-items: flex-end;
	gap: $sp1;
}

.payment-group-card__price-currency {
	@include font-size(fs-80);
	align-self: flex-end;
}

.payment-group-card__features {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: $sp4;
	width: fit-content;
	margin-bottom: $sp3;
}

.payment-group-card__feature {
	display: flex;
	align-items: center;
	gap: $sp3;
}

.payment-group-card__feature-icon {
	color: var(--color-payment-card-text);
	transition: color $trans-time-fast;
}

.payment-group-card__feature-label {
	@include font-size(fs-80);
	color: var(--color-payment-card-text);
	transition: color $trans-time-fast;
}

.payment-group-card__divider {
	width: 100%;
	height: 1px;
	background-color: var(--color-payment-card-divider);
	margin: $sp4 0;
	transition: background-color $trans-time-fast;
}

.payment-group-card__info {
	@include font-size(fs-60);
	text-align: center;
	margin-top: $sp2;
	color: var(--color-payment-card-text-info);
	transition: color $trans-time-fast;
}
</style>
